import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Sunrise, Moon, AlertTriangle, Mail, CalendarClock, MapPin, Users, ClipboardList,
  ShieldCheck, Volume2, Loader2, RefreshCw, CheckCircle2, XCircle, FileText,
  FlaskConical, Plane, Gift, ArrowUpRight, Activity, Clock, Cpu, Plus, Trash2, Pencil, X,
  UploadCloud, Download,
} from "lucide-react";
import { getBriefing, speak, getJobs, actOnDecision, addPerson, updatePerson, deletePerson, uploadFile, listFiles, deleteFile, API } from "../../lib/api";
import Markdown from "./Markdown";
import { ARIA } from "../../lib/assets";

const fmt = (d) => (d ? dayjs(d).format("MMM D, h:mm A") : "");
const fmtDate = (d) => (d ? dayjs(d).format("MMM D") : "");
const fmtTime = (d) => (d ? dayjs(d).format("h:mm A") : "");

const LEVELS = {
  1: { label: "Level 1 · Autonomous", color: "#10B981", bg: "rgba(16,185,129,0.12)" },
  2: { label: "Level 2 · Confirm", color: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
  3: { label: "Level 3 · Strong Approval", color: "#F43F5E", bg: "rgba(244,63,94,0.12)" },
};

const CAT_COLORS = {
  Critical: "#F43F5E", "Decision Required": "#F59E0B", "Reply Required": "#E2B857",
  Delegated: "#8B5CF6", "Waiting for Response": "#38BDF8", "Information Only": "#64748B",
  Personal: "#EC4899", Travel: "#00E5FF", Finance: "#10B981", Legal: "#F59E0B", HR: "#EC4899",
};

export function SectionHeader({ overline, title, desc, right }) {
  return (
    <div className="mb-8 lg:mb-10 fade-up">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="overline mb-3">{overline}</div>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-amber-50/95 leading-[1.05]">{title}</h2>
          {desc && <p className="text-slate-400/90 text-sm sm:text-base mt-3 max-w-2xl font-light leading-relaxed">{desc}</p>}
        </div>
        {right}
      </div>
      <div className="hairline mt-6" />
    </div>
  );
}

function KPI({ icon: Icon, label, value, tone, last }) {
  return (
    <div className={`relative px-5 lg:px-7 py-2 fade-up ${!last ? "sm:border-r sm:border-amber-500/10" : ""}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color: tone }} />
        <span className="overline">{label}</span>
      </div>
      <div className="font-display text-4xl lg:text-5xl font-medium tracking-tight" style={{ color: "#FDF6E3" }}>{value}</div>
    </div>
  );
}

/* ------------------------------- TODAY ---------------------------------- */
export function TodaySection({ data, onAsk }) {
  const [kind, setKind] = useState("morning");
  const [briefs, setBriefs] = useState({});
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = React.useRef(null);

  const load = async (k, force) => {
    if (briefs[k] && !force) { setKind(k); return; }
    setKind(k);
    setLoading(true);
    try {
      const b = await getBriefing(k, "en");
      setBriefs((prev) => ({ ...prev, [k]: b.content }));
    } catch (e) {
      setBriefs((prev) => ({ ...prev, [k]: "Unable to generate briefing right now." }));
    }
    setLoading(false);
  };

  useEffect(() => { load("morning"); }, []); // eslint-disable-line

  const readAloud = async () => {
    if (!briefs[kind]) return;
    setSpeaking(true);
    try {
      const audio = await speak(briefs[kind], "nova");
      audioRef.current.src = audio;
      audioRef.current.onended = () => setSpeaking(false);
      await audioRef.current.play();
    } catch (e) { setSpeaking(false); }
  };

  const criticalEmails = (data.emails || []).filter((e) => ["Critical", "Decision Required"].includes(e.category));
  const pendingDecisions = (data.decisions || []).filter((d) => d.status === "pending");
  const c = data.counts || {};
  const hour = new Date().getHours();
  const period = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";

  return (
    <div>
      <audio ref={audioRef} className="hidden" />
      <div className="relative overflow-hidden rounded-3xl mb-8 fade-up border border-amber-500/15" data-testid="today-hero">
        <img src={ARIA.hero} alt="Aria, your Chief of Staff" className="absolute inset-0 w-full h-full object-cover object-right opacity-60" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, rgba(10,9,8,0.98) 30%, rgba(10,9,8,0.72) 55%, rgba(10,9,8,0.25) 100%)" }} />
        <div className="relative p-8 sm:p-12 lg:p-14">
          <div className="flex items-center gap-3 mb-6">
            <img src={ARIA.portrait} alt="Aria" className="w-12 h-12 rounded-full object-cover border border-amber-400/40 shadow-[0_0_20px_-4px_rgba(226,184,87,0.6)]" data-testid="aria-avatar" />
            <div>
              <div className="font-display text-lg font-semibold text-amber-100 leading-none">Aria</div>
              <div className="overline text-[9px] mt-1">Your Executive Chief of Staff</div>
            </div>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-amber-50 leading-[1.02]">
            Good {period},<br />
            <span className="italic text-amber-300/90">{data.profile?.name || "Kaustubh"}.</span>
          </h1>
          <p className="text-slate-300/85 mt-5 text-base sm:text-lg font-light max-w-xl">I've prepared your day. Here is everything that needs your attention.</p>
          <div className="hairline mt-9 mb-6" />
          <div className="grid grid-cols-2 lg:grid-cols-4 -mx-5">
            <KPI icon={AlertTriangle} label="Decisions" value={c.decisions ?? 0} tone="#F43F5E" />
            <KPI icon={Mail} label="Unread Email" value={c.unread_email ?? 0} tone="#E2B857" />
            <KPI icon={CalendarClock} label="Meetings" value={c.meetings_today ?? 0} tone="#38BDF8" />
            <KPI icon={ClipboardList} label="Open Tasks" value={c.tasks_open ?? 0} tone="#10B981" last />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 -mt-2">
        <span className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300/80 border border-amber-500/20" data-testid="sample-data-badge">
          Sample workspace — demo data, fully editable
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-card p-5 fade-up">
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              {["morning", "evening"].map((k) => (
                <button
                  key={k}
                  onClick={() => load(k)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    kind === k ? "gold-btn" : "border border-white/10 text-slate-300 hover:border-amber-500/40"
                  }`}
                  data-testid={`briefing-${k}-tab`}
                >
                  {k === "morning" ? <Sunrise size={13} /> : <Moon size={13} />}
                  {k === "morning" ? "Morning Brief" : "Evening Brief"}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => load(kind, true)} className="p-2 rounded-lg hover:bg-white/10 transition" title="Regenerate" data-testid="briefing-refresh">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
              <button onClick={readAloud} className="p-2 rounded-lg hover:bg-white/10 transition" title="Read aloud" data-testid="briefing-tts">
                {speaking ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
              </button>
            </div>
          </div>
          {loading && !briefs[kind] ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center">
              <Loader2 size={16} className="animate-spin" /> Composing your executive brief…
            </div>
          ) : (
            <div className="max-h-[440px] overflow-y-auto pr-1" data-testid="briefing-content">
              <Markdown text={briefs[kind]} />
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="glass-card p-4 fade-up">
            <div className="overline mb-3 flex items-center gap-1.5"><AlertTriangle size={13} /> Needs your decision</div>
            <div className="space-y-2">
              {pendingDecisions.slice(0, 4).map((d) => (
                <div key={d.id} className="text-sm border-l-2 pl-2.5 py-0.5" style={{ borderColor: LEVELS[d.level]?.color }}>
                  <div className="font-medium leading-snug">{d.title}</div>
                  <div className="text-[11px]" style={{ color: LEVELS[d.level]?.color }}>{LEVELS[d.level]?.label}</div>
                </div>
              ))}
              {pendingDecisions.length === 0 && <div className="text-xs text-slate-500">Nothing awaiting you.</div>}
            </div>
          </div>

          <div className="glass-card p-4 fade-up">
            <div className="overline mb-3 flex items-center gap-1.5"><Mail size={13} /> Critical inbox</div>
            <div className="space-y-2.5">
              {criticalEmails.slice(0, 4).map((e) => (
                <button key={e.id} onClick={() => onAsk(`Summarize and draft a reply to this email from ${e.from}: "${e.subject}"`)}
                  className="block w-full text-left group" data-testid="today-critical-email">
                  <div className="text-sm font-medium leading-snug group-hover:text-amber-300 transition">{e.subject}</div>
                  <div className="text-[11px] text-slate-500">{e.from}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- DECISIONS -------------------------------- */
export function DecisionsSection({ data, refresh }) {
  const [busy, setBusy] = useState(null);
  const act = async (id, action) => {
    setBusy(id + action);
    await actOnDecision(id, action);
    await refresh();
    setBusy(null);
  };
  const pending = (data.decisions || []).filter((d) => d.status === "pending");
  const resolved = (data.decisions || []).filter((d) => d.status !== "pending");
  return (
    <div>
      <SectionHeader overline="Transaction Control Layer" title="Decisions & Approvals"
        desc="Nothing sensitive executes without your authorization. Higher risk requires stronger approval." />
      <div className="space-y-3">
        {pending.map((d) => {
          const L = LEVELS[d.level] || LEVELS[1];
          return (
            <div key={d.id} className="glass-card p-5 fade-up" data-testid="decision-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold mb-2"
                    style={{ color: L.color, background: L.bg }}>
                    <ShieldCheck size={12} /> {L.label}
                  </div>
                  <h3 className="font-display font-semibold text-lg">{d.title}</h3>
                  <p className="text-slate-400 text-sm mt-1">{d.summary}</p>
                  {d.reason && <p className="text-[11px] text-slate-500 mt-1.5">Why approval: {d.reason}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => act(d.id, "reject")} disabled={busy}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-white/10 text-slate-300 hover:border-rose-500/50 hover:text-rose-300 transition text-sm" data-testid={`reject-decision`}>
                    {busy === d.id + "reject" ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Decline
                  </button>
                  <button onClick={() => act(d.id, "approve")} disabled={busy}
                    className="gold-btn flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm" data-testid={`approve-decision`}>
                    {busy === d.id + "approve" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    {d.level === 3 ? "Authorize" : "Approve"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {pending.length === 0 && <div className="glass-card p-8 text-center text-slate-400">All clear. No pending approvals.</div>}

        {resolved.length > 0 && (
          <>
            <div className="overline mt-8 mb-2">Recently resolved</div>
            {resolved.map((d) => (
              <div key={d.id} className="glass-card p-4 flex items-center justify-between opacity-70">
                <span className="text-sm">{d.title}</span>
                <span className={`text-xs font-semibold ${d.status === "approved" ? "text-emerald-400" : "text-rose-400"}`}>
                  {d.status}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ AGENTS ---------------------------------- */
export function AgentsSection({ data, onAsk }) {
  const [jobs, setJobs] = useState([]);
  useEffect(() => { getJobs().then(setJobs).catch(() => {}); }, []);
  return (
    <div>
      <SectionHeader overline="Agent Command Center" title="Your Specialist Office"
        desc="One Chief of Staff orchestrating six specialist agents plus a reviewer. Full audit trail below." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {(data.agents || []).map((a) => (
          <button key={a.id} onClick={() => onAsk(`Engage the ${a.short} agent.`, a.id)}
            className="glass-card p-4 text-left fade-up group" data-testid={`agent-card-${a.id}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: a.color + "22", color: a.color }}>
                <Cpu size={16} />
              </div>
              <div className="font-display font-semibold text-sm group-hover:text-amber-300 transition">{a.short}</div>
              <ArrowUpRight size={15} className="ml-auto text-slate-500 group-hover:text-amber-300 transition" />
            </div>
            <p className="text-xs text-slate-400 mt-2.5 leading-relaxed">{a.role}</p>
          </button>
        ))}
      </div>

      <div className="overline mb-3 flex items-center gap-1.5"><Activity size={13} /> Audit trail & activity</div>
      <div className="space-y-2">
        {jobs.map((j) => (
          <div key={j.id} className="glass-card p-4 fade-up" data-testid="audit-job">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{j.title}</div>
                {j.pipeline && (
                  <div className="text-[11px] text-slate-500 mt-1 font-mono">
                    {j.pipeline.map((p) => p.replace(/_/g, " ")).join(" → ")}
                  </div>
                )}
              </div>
              <div className="text-right">
                <span className={`text-xs font-semibold ${j.status === "complete" || j.status === "approved" ? "text-emerald-400" : j.status === "rejected" ? "text-rose-400" : "text-amber-400"}`}>
                  {j.status}
                </span>
                <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5"><Clock size={10} /> {fmt(j.created)}</div>
              </div>
            </div>
            {j.outcome && <div className="text-[11px] text-slate-400 mt-2 border-t border-white/5 pt-2">{j.outcome}</div>}
          </div>
        ))}
        {jobs.length === 0 && <div className="text-xs text-slate-500">No activity logged yet. Give your office an instruction.</div>}
      </div>
    </div>
  );
}

/* ------------------------------- EMAIL ---------------------------------- */
export function EmailSection({ data, onAsk }) {
  const [cat, setCat] = useState("All");
  const cats = ["All", ...Array.from(new Set((data.emails || []).map((e) => e.category)))];
  const list = (data.emails || []).filter((e) => cat === "All" || e.category === cat);
  return (
    <div>
      <SectionHeader overline="Communication Command Center" title="Executive Inbox"
        desc="Prioritized, categorized, and ready for one-tap drafting in your style." />
      <div className="flex flex-wrap gap-2 mb-4">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${cat === c ? "gold-btn" : "border border-white/10 text-slate-300 hover:border-amber-500/40"}`}
            data-testid={`email-filter-${c.toLowerCase().replace(/\s/g, "-")}`}>
            {c}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {list.map((e) => (
          <div key={e.id} className="glass-card p-4 fade-up" data-testid="email-item">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: CAT_COLORS[e.category], background: CAT_COLORS[e.category] + "22" }}>{e.category}</span>
                  {e.unread && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                  {e.owes_me_reply && <span className="text-[10px] text-cyan-400">owes you a reply</span>}
                </div>
                <div className="font-medium mt-1.5">{e.subject}</div>
                <div className="text-[11px] text-slate-500">{e.from} · {fmt(e.received)}</div>
                <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{e.preview}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => onAsk(`Draft a reply in my style to this email from ${e.from}: "${e.subject}". Context: ${e.preview}`)}
                className="text-xs px-3 py-1.5 rounded-lg gold-btn" data-testid="email-draft-reply">Draft reply</button>
              <button onClick={() => onAsk(`Summarize this email chain from ${e.from}: "${e.subject}"`)}
                className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:border-amber-500/40 transition">Summarize</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- MEETINGS --------------------------------- */
export function MeetingsSection({ data, onAsk }) {
  return (
    <div>
      <SectionHeader overline="Calendar & Meeting Intelligence" title="Meetings"
        desc="Every important meeting comes with a pre-built executive pack." />
      <div className="space-y-3">
        {(data.meetings || []).map((m) => (
          <div key={m.id} className="glass-card p-5 fade-up" data-testid="meeting-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  {m.importance === "high" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-rose-300 bg-rose-500/15">High priority</span>}
                  <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12} /> {fmtTime(m.start)} – {fmtTime(m.end)}</span>
                </div>
                <h3 className="font-display font-semibold text-lg mt-1.5">{m.title}</h3>
                <div className="text-[12px] text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {m.location} · {fmtDate(m.start)}</div>
                <div className="text-sm text-slate-400 mt-1.5">{m.attendees?.join(", ")}</div>
                {m.notes && <p className="text-sm text-slate-400 mt-2 italic">{m.notes}</p>}
              </div>
              <button onClick={() => onAsk(`Prepare me for my meeting: "${m.title}" with ${m.attendees?.join(", ")}. Build the full executive meeting pack.`)}
                className="gold-btn px-4 py-2 rounded-xl text-sm whitespace-nowrap" data-testid="meeting-prepare">
                Prepare me
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------- TASKS ---------------------------------- */
export function TasksSection({ data }) {
  const statusTone = { pending: "#F59E0B", in_progress: "#00E5FF", done: "#10B981" };
  return (
    <div>
      <SectionHeader overline="Delegation & Follow-ups" title="Tasks & Commitments"
        desc="Owned by you or delegated to specialist agents, with deadlines tracked." />
      <div className="space-y-2">
        {(data.tasks || []).map((t) => (
          <div key={t.id} className="glass-card p-4 flex items-center justify-between gap-3 fade-up" data-testid="task-item">
            <div>
              <div className="font-medium">{t.title}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Owner: {t.owner} · Source: {t.source}</div>
            </div>
            <div className="text-right shrink-0">
              <span className="text-xs font-semibold capitalize" style={{ color: statusTone[t.status] }}>{t.status.replace("_", " ")}</span>
              <div className="text-[11px] text-slate-500">Due {fmtDate(t.due)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------ PEOPLE ---------------------------------- */
const EMPTY_PERSON = { name: "", role: "", org: "", relationship: "", birthday: "", anniversary: "", notes: "", comms_pref: "" };

export function PeopleSection({ data, onAsk, refresh }) {
  const [form, setForm] = useState(null); // null = closed, else {id?, ...fields}
  const [saving, setSaving] = useState(false);

  const people = data.people || [];

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = { ...form };
    delete payload.id;
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = k === "birthday" || k === "anniversary" ? null : ""; });
    try {
      if (form.id) await updatePerson(form.id, payload);
      else await addPerson(payload);
      setForm(null);
      await refresh();
    } catch (e) {}
    setSaving(false);
  };

  const remove = async (id) => { await deletePerson(id); refresh(); };

  const F = (label, key, type = "text") => (
    <div>
      <label className="text-[11px] text-slate-500">{label}</label>
      <input type={type} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-[#0F131C] border border-white/10 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:border-amber-500/50" data-testid={`person-field-${key}`} />
    </div>
  );

  return (
    <div>
      <SectionHeader overline="Relationship Intelligence" title="People"
        desc="Your executive CRM — birthdays, anniversaries and follow-ups, never forgotten."
        right={<button onClick={() => setForm({ ...EMPTY_PERSON })} className="gold-btn flex items-center gap-2 px-4 py-2 rounded-xl text-sm" data-testid="add-person-button"><Plus size={15} /> Add contact</button>} />

      {form && (
        <div className="glass-card p-6 mb-5 fade-up" data-testid="person-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-medium text-amber-50/95">{form.id ? "Edit contact" : "New contact"}</h3>
            <button onClick={() => setForm(null)} className="p-2 rounded-lg hover:bg-white/10 transition"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {F("Name *", "name")}
            {F("Role", "role")}
            {F("Organization", "org")}
            {F("Relationship", "relationship")}
            {F("Birthday", "birthday", "date")}
            {F("Anniversary", "anniversary", "date")}
            {F("Preferred contact", "comms_pref")}
          </div>
          <div className="mt-3">
            <label className="text-[11px] text-slate-500">Notes</label>
            <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full bg-[#0F131C] border border-white/10 rounded-lg px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:border-amber-500/50" data-testid="person-field-notes" />
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={save} disabled={saving || !form.name.trim()} className="gold-btn flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm disabled:opacity-40" data-testid="save-person">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save contact
            </button>
          </div>
        </div>
      )}

      {people.length === 0 && !form && (
        <div className="glass-card p-12 text-center fade-up">
          <Users size={30} className="mx-auto mb-3 text-amber-400/50" />
          <p className="text-slate-400 font-light">No contacts yet. Add your key people — Aria will remind you before every birthday and anniversary and offer to draft a note.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {people.map((p) => (
          <div key={p.id} className="glass-card p-5 fade-up group" data-testid="person-card">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg font-medium text-amber-50/95">{p.name}</h3>
                <div className="text-[12px] text-slate-500">{[p.role, p.org].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex items-center gap-1">
                {p.relationship && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300 mr-1">{p.relationship}</span>}
                <button onClick={() => setForm({ id: p.id, name: p.name || "", role: p.role || "", org: p.org || "", relationship: p.relationship || "", birthday: (p.birthday || "").slice(0, 10), anniversary: (p.anniversary || "").slice(0, 10), notes: p.notes || "", comms_pref: p.comms_pref || "" })}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-amber-300 hover:bg-amber-500/10 transition opacity-0 group-hover:opacity-100" data-testid="edit-person"><Pencil size={13} /></button>
                <button onClick={() => remove(p.id)} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition opacity-0 group-hover:opacity-100" data-testid="delete-person"><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-[12px] text-slate-400">
              {p.birthday && <div className="flex items-center gap-1.5"><Gift size={12} className="text-pink-400" /> Birthday {fmtDate(p.birthday)}</div>}
              {p.anniversary && <div className="flex items-center gap-1.5"><Gift size={12} className="text-amber-400" /> Anniversary {fmtDate(p.anniversary)}</div>}
              {p.comms_pref && <div>Prefers: {p.comms_pref}</div>}
            </div>
            {p.notes && <p className="text-sm text-slate-400 mt-2.5 italic font-light">{p.notes}</p>}
            {(p.birthday || p.anniversary) && (
              <button onClick={() => onAsk(`Draft a warm, personal ${p.anniversary ? "anniversary" : "birthday"} message for ${p.name} (${p.role}). Notes: ${p.notes}`)}
                className="mt-3 ghost-btn rounded-full px-3.5 py-1.5 text-xs" data-testid="draft-wishes">
                Draft wishes
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------ TRAVEL ---------------------------------- */
export function TravelSection({ data, onAsk }) {
  return (
    <div>
      <SectionHeader overline="Executive Administration & Travel" title="Travel"
        desc="One consolidated itinerary. Bookings are held and priced, then routed for your approval." />
      <div className="space-y-4">
        {(data.travel || []).map((t) => (
          <div key={t.id} className="glass-card p-5 fade-up" data-testid="travel-card">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Plane size={18} className="text-cyan-400" /> {t.trip}</h3>
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 capitalize">{t.status}</span>
            </div>
            <div className="text-[12px] text-slate-500 mt-1">{fmt(t.depart)} → {fmtDate(t.return)} · {t.weather}</div>
            <div className="mt-3 space-y-2">
              {t.segments?.map((s, i) => (
                <div key={i} className="flex items-center gap-3 text-sm border-l-2 border-cyan-500/30 pl-3 py-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-cyan-400 w-14">{s.type}</span>
                  <span className="flex-1 text-slate-300">{s.detail}</span>
                  <span className="text-[11px] text-slate-500">{s.time}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">{s.status}</span>
                </div>
              ))}
            </div>
            <div className="text-[12px] text-slate-400 mt-3">Documents: {t.documents?.join(" · ")}</div>
            {t.notes && <p className="text-sm text-amber-300/80 mt-2">{t.notes}</p>}
            <button onClick={() => onAsk(`Optimize and finalize my itinerary for "${t.trip}" and prepare the booking for approval.`)}
              className="mt-3 gold-btn px-4 py-2 rounded-xl text-sm" data-testid="travel-optimize">Optimize itinerary</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- RESEARCH --------------------------------- */
export function ResearchSection({ data, onAsk }) {
  return (
    <div>
      <SectionHeader overline="Strategy & Research" title="Research"
        desc="Executive-grade research: facts, assumptions and estimates clearly distinguished."
        right={<button onClick={() => onAsk("Run a deep-research strategic assessment. Ask me what topic if unclear.")} className="gold-btn px-4 py-2 rounded-xl text-sm">New research</button>} />
      <div className="grid gap-3">
        {(data.research || []).map((r) => (
          <div key={r.id} className="glass-card p-5 fade-up" data-testid="research-card">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold flex items-center gap-2"><FlaskConical size={16} className="text-cyan-400" /> {r.title}</h3>
              <span className={`text-xs font-semibold ${r.status === "complete" ? "text-emerald-400" : "text-amber-400"}`}>{r.status}</span>
            </div>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{r.summary}</p>
            <button onClick={() => onAsk(`Expand this research into a full board paper: "${r.title}"`)}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:border-amber-500/40 transition">Build board paper</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------- DOCUMENTS --------------------------------- */
export function DocumentsSection({ data, onAsk }) {
  const sensTone = { confidential: "#F43F5E", restricted: "#F59E0B", internal: "#38BDF8", normal: "#64748B" };
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const fileBase = API.replace(/\/api$/, "");
  const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

  const load = () => listFiles().then(setFiles).catch(() => {});
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(""); setUploading(true); setProgress(0);
    try {
      await uploadFile(file, setProgress);
      await load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Upload failed. Please try again.");
    }
    setUploading(false); setProgress(0);
  };

  const remove = async (id) => { await deleteFile(id); load(); };

  return (
    <div>
      <SectionHeader overline="Document Intelligence" title="Executive Vault"
        desc="Permissioned documents with AI summaries and clause-level analysis." />

      <div className="glass-card p-5 mb-6 fade-up" data-testid="file-upload-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-medium flex items-center gap-2"><UploadCloud size={17} className="text-amber-400" /> Upload files &amp; media</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Securely stored in your private object storage. PDFs, images, docs — up to 25 MB.</div>
          </div>
          <label className="gold-btn flex items-center gap-2 px-4 py-2 rounded-xl text-sm cursor-pointer" data-testid="file-upload-button">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
            {uploading ? `Uploading ${progress}%` : "Choose file"}
            <input type="file" className="hidden" onChange={onPick} disabled={uploading} data-testid="file-input" />
          </label>
        </div>
        {err && <div className="text-rose-400 text-sm mt-2" data-testid="file-upload-error">{err}</div>}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-3 bg-[#0F131C] border border-white/10 rounded-lg px-3 py-2" data-testid="uploaded-file">
                <a href={`${fileBase}${f.download_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 text-sm hover:text-amber-300 transition">
                  <FileText size={15} className="text-amber-400 shrink-0" />
                  <span className="truncate">{f.original_filename}</span>
                  <span className="text-[11px] text-slate-500 shrink-0">{kb(f.size)}</span>
                </a>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={`${fileBase}${f.download_url}`} download={f.original_filename} className="p-1.5 rounded-lg hover:bg-white/10 transition" title="Download"><Download size={14} /></a>
                  <button onClick={() => remove(f.id)} className="p-1.5 rounded-lg hover:bg-white/10 transition text-slate-400 hover:text-rose-400" title="Remove" data-testid="delete-file"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {(data.documents || []).map((d) => (
          <div key={d.id} className="glass-card p-4 fade-up" data-testid="document-item">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <FileText size={18} className="text-amber-400 mt-0.5" />
                <div>
                  <div className="font-medium">{d.name}</div>
                  <div className="text-[11px] text-slate-500">{d.type} · Owner: {d.owner} · Updated {fmtDate(d.updated)}</div>
                  <p className="text-sm text-slate-400 mt-1">{d.summary}</p>
                </div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ color: sensTone[d.sensitivity], background: sensTone[d.sensitivity] + "22" }}>{d.sensitivity}</span>
            </div>
            <button onClick={() => onAsk(`Analyze the document "${d.name}" — extract key clauses, risks, obligations and next steps.`)}
              className="mt-2.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:border-amber-500/40 transition" data-testid="document-analyze">Analyze</button>
          </div>
        ))}
      </div>
    </div>
  );
}
